import React, { useCallback, useMemo } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { getFormatTemplates, buildApplyFormatTemplate } from '../../../lib/services/format-template.service';
import { useCompanySport } from '../../../lib/stores/company.store';
import { suggestTemplate } from '../../../lib/helpers/format-template.helpers';
import { SPORT_HAS_GOALIES } from '../../../lib/constants';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { messages } from '../format-template.messages';
import { UseTemplateModalGraphQL } from './use-template-modal.graphql';
import { IUseFormatTemplateFields } from './use-template-modal.types';
import { validationSchema } from './use-template-modal.validation';

interface IUseTemplateModalContainerProps {
  open: boolean;
  onClose: () => void;
  assessmentId: string;
  ageGroup?: IAgeGroup | null;
  companyId: string;
}

const initialValues: IUseFormatTemplateFields = {
  bucket: null,
  templateId: null,
  bandKey: null,
  previewStageCount: null,
  applyMode: 'REPLACE',
};

export const UseTemplateModalContainer: React.FC<IUseTemplateModalContainerProps> = ({
  open,
  onClose,
  assessmentId,
  ageGroup,
  companyId,
}) => {
  const { templates, loading } = getFormatTemplates(companyId);
  const { applyFormatTemplate } = buildApplyFormatTemplate();

  const ageGroupBucket = ageGroup?.bucket ?? null;
  // Goalie-splitting sports match bands on skaters only (degrading to the total when numGoalies
  // isn't selected); a sport without goalies matches on the whole roster.
  const hasGoalies = SPORT_HAS_GOALIES[useCompanySport()];
  const nonGoalieCount = ageGroup?.numPlayers != null
    ? ageGroup.numPlayers - (hasGoalies ? ageGroup.numGoalies ?? 0 : 0)
    : undefined;
  const recommended = useMemo(
    () => suggestTemplate(templates, ageGroupBucket, nonGoalieCount),
    [templates, ageGroupBucket, nonGoalieCount],
  );

  const handleSubmit = useCallback(async (
    values: IUseFormatTemplateFields,
    { setSubmitting, setStatus }: FormikHelpers<IUseFormatTemplateFields>,
  ) => {
    setStatus(undefined);
    if (!ageGroup?._id) { setStatus(<IntlMsg {...messages.selectAgeGroupStatus} />); setSubmitting(false); return; }
    try {
      await applyFormatTemplate({
        assessmentId,
        templateId: values.templateId as string,
        ageGroupId: ageGroup._id,
        mode: values.applyMode,
        bandKey: values.bandKey,
      });
      onClose();
    } catch (err) {
      console.error('applyFormatTemplate failed:', err);
      setStatus(<IntlMsg {...messages.applyError} />);
    } finally {
      setSubmitting(false);
    }
  }, [assessmentId, ageGroup, applyFormatTemplate, onClose]);

  return (
    // key resets the form each time the modal reopens — the Formik provider stays mounted.
    <Formik<IUseFormatTemplateFields>
      key={open ? 'open' : 'closed'}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {form => (
        <UseTemplateModalGraphQL
          open={open}
          onClose={onClose}
          templates={templates}
          templatesLoading={loading}
          ageGroupId={ageGroup?._id}
          ageGroupBucket={ageGroupBucket}
          recommended={recommended}
          form={form}
        />
      )}
    </Formik>
  );
};
